import React, { useEffect, useRef, useState } from 'react';
import { Plus, CreditCard as Edit, Trash2, Clock, CheckCircle, Play, MapPin, Phone, CheckSquare, X, Table, Users, LayoutGrid as Layout, Download } from 'lucide-react';

import { supabase } from '../lib/supabase';
import { sampleTables } from '../data/sampleDayOf';
import {
  getTimelineEvents,
  addTimelineEvent,
  updateTimelineEvent,
  deleteTimelineEvent,
  getUserGuestList,
  getVendors,
  addVendor,
  updateVendor,
  deleteVendor,
} from '../lib/dayof';
import type { WeddingTimelineEvent, WeddingVendor, SeatingTable } from '../types/dayof';
import type { GuestInfo } from '../types/eventPlanner';
import EventLayoutPlanner from '../components/EventLayoutPlanner';

export default function DayOfPlanning() {
  const sparklesRef = useRef<HTMLDivElement>(null);
  const [timeline, setTimeline] = useState<WeddingTimelineEvent[]>([]);
  const [vendors, setVendors] = useState<WeddingVendor[]>([]);
  const [tables, setTables] = useState<SeatingTable[]>(sampleTables);
  const [guests, setGuests] = useState<string[]>([]);
  const [guestInfo, setGuestInfo] = useState<GuestInfo[]>([]);
  const [attendingGuests, setAttendingGuests] = useState<GuestInfo[]>([]);
  const [notAttendingGuests, setNotAttendingGuests] = useState<GuestInfo[]>([]);
  const [selectedTable, setSelectedTable] = useState<number | null>(null);
  const [draggedGuest, setDraggedGuest] = useState<string | null>(null);
  const [registryId, setRegistryId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'timeline' | 'vendors' | 'seating' | 'layout'>('timeline');
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [editingEvent, setEditingEvent] = useState<WeddingTimelineEvent | null>(null);
  const [editingVendor, setEditingVendor] = useState<WeddingVendor | null>(null);
  const [newEvent, setNewEvent] = useState({
    time: '',
    event: '',
    duration: '',
    location: '',
    responsible: '',
    notes: '',
  });
  const [newVendor, setNewVendor] = useState({
    name: '',
    contact: '',
    role: '',
    arrival_time: '',
    setup_time: '',
    notes: '',
    status: 'pending' as 'confirmed' | 'pending' | 'cancelled',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const createSparkle = () => {
      if (!sparklesRef.current) return;

      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';

      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);

      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;

      sparklesRef.current.appendChild(sparkle);

      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadTimelineEvents();
    loadGuestList();
    loadGuestInfo();
    loadVendors();
    loadUserRegistry();
  }, []);

  const loadUserRegistry = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get the user's most recent registry
      const { data: registry, error: registryError } = await supabase
        .from('registries')
        .select('id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (registryError || !registry) return;
      setRegistryId(registry.id);
    } catch (error) {
      console.error('Error loading user registry:', error);
    }
  };

  const loadTimelineEvents = async () => {
    try {
      setLoading(true);
      const events = await getTimelineEvents();
      setTimeline(events);
    } catch (error) {
      console.error('Error loading timeline events:', error);
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  };

  const loadGuestList = async () => {
    try {
      const guestList = await getUserGuestList();
      setGuests(guestList);
    } catch (error) {
      console.error('Error loading guest list:', error);
    }
  };

  const loadGuestInfo = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get the user's most recent registry
      const { data: registry, error: registryError } = await supabase
        .from('registries')
        .select('id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (registryError || !registry) return;

      // Get guest tables and RSVPs for this registry
      const [guestTablesResult, rsvpResult] = await Promise.all([
        supabase
          .from('guest_tables')
          .select('guest_name, table_number')
          .eq('registry_id', registry.id),
        supabase
          .from('registry_rsvps')
          .select('name, email, status')
          .eq('registry_id', registry.id)
      ]);

      const guestTables = guestTablesResult.data || [];
      const rsvps = rsvpResult.data || [];

      // Combine guest data
      const combinedGuests: GuestInfo[] = guestTables.map(guest => {
        const rsvp = rsvps.find(r => r.name.toLowerCase() === guest.guest_name.toLowerCase());
        return {
          id: `guest-${guest.guest_name}`,
          name: guest.guest_name,
          email: rsvp?.email,
          tableAssignment: guest.table_number > 0 ? `Table ${guest.table_number}` : undefined,
          rsvpStatus: (rsvp?.status as 'attending' | 'not_attending') || 'pending'
        };
      });

      setGuestInfo(combinedGuests);
      
      // Separate guests by RSVP status
      const attending = combinedGuests.filter(guest => guest.rsvpStatus === 'attending');
      const notAttending = combinedGuests.filter(guest => guest.rsvpStatus === 'not_attending');
      
      setAttendingGuests(attending);
      setNotAttendingGuests(notAttending);
      
      // Update unassigned guests list to only include attending guests
      const unassignedAttending = attending
        .filter(guest => !guest.tableAssignment)
        .map(guest => guest.name);
      setGuests(unassignedAttending);
    } catch (error) {
      console.error('Error loading guest info:', error);
    }
  };

  const loadVendors = async () => {
    try {
      const userVendors = await getVendors();
      setVendors(userVendors);
    } catch (error) {
      console.error('Error loading vendors:', error);
      setVendors([]);
    }
  };

  const updateEventStatus = async (eventId: string, status: 'pending' | 'in_progress' | 'completed') => {
    try {
      await updateTimelineEvent(eventId, { status });
      setTimeline(prev => prev.map(event => (event.id === eventId ? { ...event, status } : event)));
    } catch (error) {
      console.error('Error updating event status:', error);
    }
  };

  const handleAddEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const eventData: Omit<WeddingTimelineEvent, 'id'> = {
        time: newEvent.time,
        event: newEvent.event,
        status: 'pending',
        duration: newEvent.duration ? parseInt(newEvent.duration, 10) : undefined,
        location: newEvent.location || undefined,
        responsible: newEvent.responsible ? newEvent.responsible.split(',').map(r => r.trim()) : undefined,
        notes: newEvent.notes || undefined,
      };

      const addedEvent = await addTimelineEvent(eventData);
      if (addedEvent) {
        setTimeline(prev => [...prev, addedEvent].sort((a, b) => a.time.localeCompare(b.time)));
      }

      setNewEvent({ time: '', event: '', duration: '', location: '', responsible: '', notes: '' });
      setShowAddEvent(false);
    } catch (error) {
      console.error('Error adding event:', error);
    }
  };

  const handleEditEvent = (event: WeddingTimelineEvent) => {
    setEditingEvent(event);
    setNewEvent({
      time: event.time,
      event: event.event,
      duration: event.duration?.toString() || '',
      location: event.location || '',
      responsible: event.responsible?.join(', ') || '',
      notes: event.notes || '',
    });
  };

  const handleUpdateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEvent) return;

    try {
      const updates = {
        time: newEvent.time,
        event: newEvent.event,
        duration: newEvent.duration ? parseInt(newEvent.duration, 10) : undefined,
        location: newEvent.location || undefined,
        responsible: newEvent.responsible ? newEvent.responsible.split(',').map(r => r.trim()) : undefined,
        notes: newEvent.notes || undefined,
      };

      await updateTimelineEvent(editingEvent.id, updates);
      setTimeline(prev =>
        prev.map(event => (event.id === editingEvent.id ? { ...event, ...updates } : event)).sort((a, b) => a.time.localeCompare(b.time))
      );

      setEditingEvent(null);
      setNewEvent({ time: '', event: '', duration: '', location: '', responsible: '', notes: '' });
    } catch (error) {
      console.error('Error updating event:', error);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await deleteTimelineEvent(eventId);
      setTimeline(prev => prev.filter(event => event.id !== eventId));
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const handleAddVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const vendorData: Omit<WeddingVendor, 'id' | 'user_id' | 'created_at'> = {
        name: newVendor.name,
        contact: newVendor.contact,
        role: newVendor.role,
        arrival_time: newVendor.arrival_time,
        setup_time: newVendor.setup_time || undefined,
        notes: newVendor.notes || undefined,
        status: newVendor.status,
      };

      const addedVendor = await addVendor(vendorData);
      if (addedVendor) {
        setVendors(prev => [...prev, addedVendor]);
      }

      setNewVendor({
        name: '',
        contact: '',
        role: '',
        arrival_time: '',
        setup_time: '',
        notes: '',
        status: 'pending',
      });
      setShowAddVendor(false);
    } catch (error) {
      console.error('Error adding vendor:', error);
    }
  };

  const handleEditVendor = (vendor: WeddingVendor) => {
    setEditingVendor(vendor);
    setNewVendor({
      name: vendor.name,
      contact: vendor.contact,
      role: vendor.role,
      arrival_time: vendor.arrival_time,
      setup_time: vendor.setup_time || '',
      notes: vendor.notes || '',
      status: vendor.status,
    });
  };

  const handleUpdateVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingVendor) return;

    try {
      const updates = {
        name: newVendor.name,
        contact: newVendor.contact,
        role: newVendor.role,
        arrival_time: newVendor.arrival_time,
        setup_time: newVendor.setup_time || undefined,
        notes: newVendor.notes || undefined,
        status: newVendor.status,
      };

      await updateVendor(editingVendor.id, updates);
      setVendors(prev => prev.map(vendor => (vendor.id === editingVendor.id ? { ...vendor, ...updates } : vendor)));

      setEditingVendor(null);
      setNewVendor({
        name: '',
        contact: '',
        role: '',
        arrival_time: '',
        setup_time: '',
        notes: '',
        status: 'pending',
      });
    } catch (error) {
      console.error('Error updating vendor:', error);
    }
  };

  const handleDeleteVendor = async (vendorId: string) => {
    try {
      await deleteVendor(vendorId);
      setVendors(prev => prev.filter(vendor => vendor.id !== vendorId));
    } catch (error) {
      console.error('Error deleting vendor:', error);
    }
  };

  const handleGuestDrop = (tableId: number, guestName: string) => {
    console.log('Dropping guest:', guestName, 'to table:', tableId);
    
    const table = tables.find(t => t.id === tableId);
    if (!table || table.guests.length >= table.capacity) {
      console.log('Cannot drop guest - table full or not found');
      return;
    }

    // Remove guest from other tables first
    setTables(prev => prev.map(t => ({
      ...t,
      guests: t.guests.filter(g => g !== guestName),
    })));

    // Add guest to the target table
    setTables(prev => prev.map(t => 
      t.id === tableId ? { ...t, guests: [...t.guests, guestName] } : t
    ));
    
    // Remove from unassigned list
    setGuests(prev => prev.filter(g => g !== guestName));
    
    console.log('Guest drop completed. New unassigned guests count:', guests.length - 1);
  };

  const removeGuestFromTable = (tableId: number, guestName: string) => {
    console.log('Removing guest:', guestName, 'from table:', tableId);
    
    // Remove guest from table
    setTables(prev => prev.map(t => 
      t.id === tableId ? { ...t, guests: t.guests.filter(g => g !== guestName) } : t
    ));
    
    // Only add back to unassigned if they are attending
    const guestInfo = attendingGuests.find(g => g.name === guestName);
    if (guestInfo && guestInfo.rsvpStatus === 'attending') {
      setGuests(prev => [...prev, guestName]);
    }
    
    console.log('Guest removal completed. New unassigned guests count:', guests.length + 1);
  };

  const addNewTable = () => {
    const newTableId = Math.max(...tables.map(t => t.id)) + 1;
    const newTable: SeatingTable = {
      id: newTableId,
      capacity: 20,
      shape: 'round',
      guests: [],
      position: { x: 100, y: 100 }
    };
    setTables(prev => [...prev, newTable]);
    console.log('Added new table with ID:', newTableId);
  };

  const updateTableCapacity = (tableId: number, newCapacity: number) => {
    console.log('DayOfPlanning - updateTableCapacity called:', { tableId, newCapacity });
    
    setTables(prev => prev.map(table => {
      if (table.id === tableId) {
        const updatedTable = { ...table, capacity: newCapacity };
        
        // If new capacity is less than current guests, move excess guests back to unassigned
        if (table.guests.length > newCapacity) {
          const excessGuests = table.guests.slice(newCapacity);
          const guestsToMove = excessGuests.map(guestName => 
            guestInfo.find(g => g.name === guestName)
          ).filter(Boolean) as GuestInfo[];
          
          console.log('DayOfPlanning - Moving excess guests back to unassigned:', guestsToMove.map(g => g.name));
          
          setGuests(prevGuests => [...prevGuests, ...guestsToMove]);
          updatedTable.guests = table.guests.slice(0, newCapacity);
        }
        
        return updatedTable;
      }
      return table;
    }));
  };

  const downloadSeatingChart = () => {
    // Prepare data for download
    const seatingData = tables.map(table => ({
      'Table Number': table.id,
      'Capacity': table.capacity,
      'Assigned Guests': table.guests.length,
      'Guest Names': table.guests.join(', '),
      'Available Seats': table.capacity - table.guests.length
    }));

    // Add summary data
    const summaryData = [
      { 'Table Number': 'SUMMARY', 'Capacity': '', 'Assigned Guests': '', 'Guest Names': '', 'Available Seats': '' },
      { 'Table Number': 'Total Tables', 'Capacity': tables.length, 'Assigned Guests': '', 'Guest Names': '', 'Available Seats': '' },
      { 'Table Number': 'Total Capacity', 'Capacity': tables.reduce((sum, t) => sum + t.capacity, 0), 'Assigned Guests': '', 'Guest Names': '', 'Available Seats': '' },
      { 'Table Number': 'Assigned Guests', 'Capacity': tables.reduce((sum, t) => sum + t.guests.length, 0), 'Assigned Guests': '', 'Guest Names': '', 'Available Seats': '' },
      { 'Table Number': 'Unassigned Guests', 'Capacity': guests.length, 'Assigned Guests': '', 'Guest Names': '', 'Available Seats': '' },
      { 'Table Number': 'Attending Guests', 'Capacity': attendingGuests.length, 'Assigned Guests': '', 'Guest Names': '', 'Available Seats': '' },
      { 'Table Number': 'Not Attending', 'Capacity': notAttendingGuests.length, 'Assigned Guests': '', 'Guest Names': '', 'Available Seats': '' }
    ];

    const allData = [...seatingData, ...summaryData];

    // Convert to CSV
    const headers = Object.keys(allData[0]);
    const csvContent = [
      headers.join(','),
      ...allData.map(row => headers.map(header => `"${row[header as keyof typeof row]}"`).join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `seating-chart-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'in_progress':
        return <Play className="w-4 h-4 text-blue-500" />;
      case 'pending':
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Sparkles Container */}
      <div ref={sparklesRef} className="sparkles-container" />

      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-white">Wedding Day Coordination</h1>
        <p className="text-xl text-gray-300 max-w-2xl mx-auto">Organize every detail for your perfect wedding day</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-800/50 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('timeline')}
          className={`flex-1 px-4 py-2 rounded-md transition-colors ${
            activeTab === 'timeline' ? 'bg-primary text-white' : 'text-gray-300 hover:text-white'
          }`}
        >
          Timeline
        </button>
        <button
          onClick={() => setActiveTab('vendors')}
          className={`flex-1 px-4 py-2 rounded-md transition-colors ${
            activeTab === 'vendors' ? 'bg-primary text-white' : 'text-gray-300 hover:text-white'
          }`}
        >
          Vendors
        </button>
        <button
          onClick={() => setActiveTab('seating')}
          className={`flex-1 px-4 py-2 rounded-md transition-colors ${
            activeTab === 'seating' ? 'bg-primary text-white' : 'text-gray-300 hover:text-white'
          }`}
        >
          Seating Chart
        </button>
        <button
          onClick={() => setActiveTab('layout')}
          className={`flex-1 px-4 py-2 rounded-md transition-colors ${
            activeTab === 'layout' ? 'bg-primary text-white' : 'text-gray-300 hover:text-white'
          }`}
        >
          Layout Planner
        </button>
      </div>

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white">Event Day Planning</h2>
            <p className="text-gray-300 mt-2">Organize your wedding day timeline and coordinate with vendors</p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-white/70">
                {timeline.filter(e => e.status === 'completed').length} of {timeline.length} events completed
              </div>
              <button
                onClick={() => setShowAddEvent(true)}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors w-full sm:w-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Add Event</span>
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : (
              <div className="space-y-4">
                {timeline.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 text-white/40 mx-auto mb-4" />
                    <p className="text-white/70 mb-4">No timeline events yet</p>
                    <button
                      onClick={() => setShowAddEvent(true)}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600"
                    >
                      Create Your First Event
                    </button>
                  </div>
                ) : (
                  timeline.map(item => (
                    <div
                      key={item.id}
                      className={`flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4 p-4 rounded-lg transition-colors ${
                        item.status === 'completed'
                          ? 'bg-green-900/30 hover:bg-green-900/50'
                          : item.status === 'in_progress'
                          ? 'bg-blue-900/30 hover:bg-blue-900/50'
                          : 'bg-gray-900/30 hover:bg-gray-800/50'
                      }`}
                    >
                      <div className="w-full sm:w-24 text-left">
                        <span className="text-sm font-medium text-luxury-gold-400">{item.time}</span>
                        {item.duration && <p className="text-xs text-white/50">{item.duration}min</p>}
                      </div>

                      <div className="flex-1">
                        <p className="font-medium text-white">{item.event}</p>
                        {item.location && (
                          <p className="text-sm text-white/70 flex items-center space-x-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            <span>{item.location}</span>
                          </p>
                        )}
                        {item.responsible && (
                          <p className="text-xs text-white/50 mt-1">
                            Responsible: {item.responsible.join(', ')}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center justify-start space-x-2 flex-wrap gap-2 mt-2 sm:mt-0">
                        <button
                          onClick={() => {
                            const nextStatus =
                              item.status === 'pending'
                                ? 'in_progress'
                                : item.status === 'in_progress'
                                ? 'completed'
                                : 'pending';
                            updateEventStatus(item.id, nextStatus);
                          }}
                          className="p-2 hover:bg-gray-700/50 rounded"
                        >
                          {getStatusIcon(item.status)}
                        </button>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                          {item.status.replace('_', ' ').charAt(0).toUpperCase() +
                            item.status.replace('_', ' ').slice(1)}
                        </span>
                        <button
                          onClick={() => handleEditEvent(item)}
                          className="p-2 hover:bg-gray-700/50 rounded text-white/70 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(item.id)}
                          className="p-2 hover:bg-gray-700/50 rounded text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Event Modal */}
      {(showAddEvent || editingEvent) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] overflow-y-auto">
          <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-2xl w-full mx-auto my-auto min-h-fit">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                {editingEvent ? 'Edit Event' : 'Add New Event'}
              </h3>
              <button
                onClick={() => {
                  setShowAddEvent(false);
                  setEditingEvent(null);
                  setNewEvent({ time: '', event: '', duration: '', location: '', responsible: '', notes: '' });
                }}
                className="p-2 hover:bg-gray-700 rounded-full text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={editingEvent ? handleUpdateEvent : handleAddEvent} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Time *</label>
                  <input
                    type="time"
                    value={newEvent.time}
                    onChange={e => setNewEvent({ ...newEvent, time: e.target.value })}
                    className="luxury-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Duration (min)</label>
                  <input
                    type="number"
                    value={newEvent.duration}
                    onChange={e => setNewEvent({ ...newEvent, duration: e.target.value })}
                    className="luxury-input"
                    min={1}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Event Name *</label>
                <input
                  type="text"
                  value={newEvent.event}
                  onChange={e => setNewEvent({ ...newEvent, event: e.target.value })}
                  className="luxury-input"
                  placeholder="e.g., Ceremony Begins"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Location</label>
                <input
                  type="text"
                  value={newEvent.location}
                  onChange={e => setNewEvent({ ...newEvent, location: e.target.value })}
                  className="luxury-input"
                  placeholder="e.g., Main Ceremony Area"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Responsible People</label>
                <input
                  type="text"
                  value={newEvent.responsible}
                  onChange={e => setNewEvent({ ...newEvent, responsible: e.target.value })}
                  className="luxury-input"
                  placeholder="e.g., Wedding Planner, Photographer (comma separated)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Notes</label>
                <textarea
                  value={newEvent.notes}
                  onChange={e => setNewEvent({ ...newEvent, notes: e.target.value })}
                  className="luxury-input"
                  rows={3}
                  placeholder="Additional notes or instructions..."
                />
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddEvent(false);
                    setEditingEvent(null);
                    setNewEvent({ time: '', event: '', duration: '', location: '', responsible: '', notes: '' });
                  }}
                  className="px-4 py-2 text-white/70 hover:text-white"
                >
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600">
                  {editingEvent ? 'Update Event' : 'Add Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vendors Tab */}
      {activeTab === 'vendors' && (
        <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Phone className="w-6 h-6 text-luxury-gold-400" />
              <h2 className="text-2xl font-bold text-white">Vendor Coordination</h2>
            </div>
            <button
              onClick={() => setShowAddVendor(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Vendor</span>
            </button>
          </div>

          <div className="space-y-4">
            {vendors.map((vendor, index) => (
              <div
                key={vendor.id || index}
                className="p-4 bg-gray-900/50 border border-gray-700 rounded-lg hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 mb-2">
                  <h3 className="font-semibold text-white">{vendor.name}</h3>
                  <div className="flex items-center justify-center sm:justify-end space-x-2 flex-wrap gap-2">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        vendor.status === 'confirmed'
                          ? 'bg-green-100 text-green-800'
                          : vendor.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {vendor.status.charAt(0).toUpperCase() + vendor.status.slice(1)}
                    </span>
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">{vendor.role}</span>
                    <button
                      onClick={() => handleEditVendor(vendor)}
                      className="p-1 hover:bg-gray-700/50 rounded text-white/70 hover:text-white"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteVendor(vendor.id)}
                      className="p-1 hover:bg-gray-700/50 rounded text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm text-white/70">
                  <div className="flex items-center space-x-1">
                    <Phone className="w-4 h-4" />
                    <span>{vendor.contact}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>Arrives: {vendor.arrival_time}</span>
                  </div>
                  {vendor.setup_time && (
                    <div className="flex items-center space-x-1">
                      <CheckSquare className="w-4 h-4" />
                      <span>Setup: {vendor.setup_time}</span>
                    </div>
                  )}
                </div>

                {vendor.notes && (
                  <div className="mt-2 p-2 bg-yellow-900/30 rounded text-sm text-yellow-200">
                    <strong>Note:</strong> {vendor.notes}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add/Edit Vendor Modal */}
          {(showAddVendor || editingVendor) && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] overflow-y-auto">
              <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-2xl w-full mx-auto my-auto min-h-fit">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">
                    {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowAddVendor(false);
                      setEditingVendor(null);
                      setNewVendor({
                        name: '',
                        contact: '',
                        role: '',
                        arrival_time: '',
                        setup_time: '',
                        notes: '',
                        status: 'pending',
                      });
                    }}
                    className="p-2 hover:bg-gray-700 rounded-full text-gray-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={editingVendor ? handleUpdateVendor : handleAddVendor} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Vendor Name *</label>
                    <input
                      type="text"
                      value={newVendor.name}
                      onChange={e => setNewVendor({ ...newVendor, name: e.target.value })}
                      className="luxury-input"
                      placeholder="e.g., Capture Moments Photography"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">Contact *</label>
                      <input
                        type="text"
                        value={newVendor.contact}
                        onChange={e => setNewVendor({ ...newVendor, contact: e.target.value })}
                        className="luxury-input"
                        placeholder="+94 77 123 4567"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">Role *</label>
                      <select
                        value={newVendor.role}
                        onChange={e => setNewVendor({ ...newVendor, role: e.target.value })}
                        className="luxury-input"
                        required
                      >
                        <option value="">Select role</option>
                        <option value="Photographer">Photographer</option>
                        <option value="DJ/Music">DJ/Music</option>
                        <option value="Catering">Catering</option>
                        <option value="Florist">Florist</option>
                        <option value="Decorator">Decorator</option>
                        <option value="Transportation">Transportation</option>
                        <option value="Makeup Artist">Makeup Artist</option>
                        <option value="Wedding Planner">Wedding Planner</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">Arrival Time *</label>
                      <input
                        type="time"
                        value={newVendor.arrival_time}
                        onChange={e => setNewVendor({ ...newVendor, arrival_time: e.target.value })}
                        className="luxury-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">Setup Time</label>
                      <input
                        type="text"
                        value={newVendor.setup_time}
                        onChange={e => setNewVendor({ ...newVendor, setup_time: e.target.value })}
                        className="luxury-input"
                        placeholder="e.g., 2:00 PM - 3:00 PM"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Status</label>
                    <select
                      value={newVendor.status}
                      onChange={e =>
                        setNewVendor({ ...newVendor, status: e.target.value as 'confirmed' | 'pending' | 'cancelled' })
                      }
                      className="luxury-input"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Notes</label>
                    <textarea
                      value={newVendor.notes}
                      onChange={e => setNewVendor({ ...newVendor, notes: e.target.value })}
                      className="luxury-input"
                      rows={3}
                      placeholder="Additional notes or special requirements..."
                    />
                  </div>

                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddVendor(false);
                        setEditingVendor(null);
                        setNewVendor({
                          name: '',
                          contact: '',
                          role: '',
                          arrival_time: '',
                          setup_time: '',
                          notes: '',
                          status: 'pending',
                        });
                      }}
                      className="px-4 py-2 text-white/70 hover:text-white"
                    >
                      Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600">
                      {editingVendor ? 'Update Vendor' : 'Add Vendor'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Seating Chart Builder */}
      {activeTab === 'seating' && (
        <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Table className="w-6 h-6 text-luxury-gold-400" />
              <h2 className="text-2xl font-bold text-white">Interactive Seating Chart</h2>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-white/70">{guests.length} unassigned</span>
              <button
                onClick={downloadSeatingChart}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Download Chart</span>
              </button>
              <button
                onClick={addNewTable}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Table className="w-4 h-4" />
                <span>Add Table</span>
              </button>
              <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors">
                Auto-Arrange
              </button>
            </div>
          </div>

          {guests.length === 0 && guestInfo.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-white/40 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Guest List Found</h3>
              <p className="text-white/70 mb-4">Create a registry with a guest list to use the seating chart feature.</p>
              <button
                onClick={() => (window.location.href = '/create')}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600"
              >
                Create Registry
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Unassigned Guests */}
              <div className="bg-gray-900/50 rounded-lg p-4 space-y-4">
                <div>
                  <h3 className="font-semibold text-white mb-2">Guest Management</h3>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        <span className="text-gray-300">Attending</span>
                      </div>
                      <span className="text-white">{attendingGuests.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="text-gray-300">Not Attending</span>
                      </div>
                      <span className="text-white">{notAttendingGuests.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-gray-300">Pending</span>
                      </div>
                      <span className="text-white">
                        {guestInfo.filter(g => g.rsvpStatus === 'pending').length}
                      </span>
                    </div>
                  </div>
                  <h3 className="font-semibold text-white mb-2">Attending Guests ({attendingGuests.length})</h3>
                  <div className="text-xs text-gray-400 mb-2">
                    Unassigned: {guests.length} | Assigned: {attendingGuests.length - guests.length}
                  </div>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {guests.map((guest, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={() => setDraggedGuest(guest)}
                      onDragEnd={() => setDraggedGuest(null)}
                      className="p-3 bg-gray-800/50 rounded-lg border border-gray-600 cursor-move hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        <span className="text-sm text-white">{guest}</span>
                      </div>
                    </div>
                  ))}
                  {guests.length === 0 && attendingGuests.length > 0 && (
                    <div className="text-center py-4 text-gray-400 text-sm">
                      All attending guests have been assigned to tables
                    </div>
                  )}
                </div>
              </div>

              {/* Seating Chart */}
              <div className="lg:col-span-3">
                <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                  {tables.map(table => (
                    <div
                      key={table.id}
                      onClick={() => setSelectedTable(selectedTable === table.id ? null : table.id)}
                      onDrop={e => {
                        e.preventDefault();
                        if (draggedGuest && table.guests.length < table.capacity && !table.guests.includes(draggedGuest)) {
                          handleGuestDrop(table.id, draggedGuest);
                        }
                        setDraggedGuest(null);
                      }}
                      onDragOver={e => e.preventDefault()}
                      className={`aspect-square rounded-lg border-2 p-3 cursor-pointer transition-all ${
                        selectedTable === table.id
                          ? 'border-primary bg-primary-50 shadow-lg'
                          : table.guests.length === table.capacity
                          ? 'border-green-400 bg-green-900/30'
                          : 'border-gray-600 hover:border-gray-500 bg-gray-900/30 hover:bg-gray-800/50'
                      }`}
                    >
                      <div className="text-center h-full flex flex-col justify-between">
                        <div>
                          <p className="font-semibold text-white mb-1">Table {table.id}</p>
                          <p
                            className={`text-xs mb-2 ${
                              table.guests.length === table.capacity ? 'text-green-400 font-medium' : 'text-white/70'
                            }`}
                          >
                            {table.guests.length}/{table.capacity}
                          </p>
                        </div>

                        <div className="space-y-1 flex-1 overflow-hidden">
                          {table.guests.map(guest => {
                            const guestInfo = attendingGuests.find(g => g.name === guest);
                            const rsvpStatus = guestInfo?.rsvpStatus || 'pending';
                            return (
                              <div
                                key={guest}
                                className={`text-xs rounded px-1 py-0.5 text-white cursor-pointer hover:bg-red-900/30 ${
                                  rsvpStatus === 'attending' ? 'bg-green-800/50' :
                                  rsvpStatus === 'not_attending' ? 'bg-red-800/50' :
                                  'bg-gray-800/50'
                                }`}
                                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                                  e.stopPropagation();
                                  removeGuestFromTable(table.id, guest);
                                }}
                                title="Click to remove from table"
                              >
                                <div className="flex items-center space-x-1">
                                  <div className={`w-1 h-1 rounded-full ${
                                    rsvpStatus === 'attending' ? 'bg-green-400' :
                                    rsvpStatus === 'not_attending' ? 'bg-red-400' :
                                    'bg-yellow-400'
                                  }`}></div>
                                  <span>{guest}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Layout Planner Tab */}
      {activeTab === 'layout' && (
        <div className="space-y-6">
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center space-x-2">
              <Layout className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-white">Interactive Event Layout Planner</h2>
            </div>
            <p className="text-gray-300">
              Design your event layout by dragging and dropping tables, stage, and other elements
            </p>
          </div>
          
          {registryId ? (
            <EventLayoutPlanner 
              registryId={registryId} 
              guests={guestInfo}
              onLayoutChange={(layout) => console.log('Layout changed:', layout)}
            />
          ) : (
            <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-8 text-center">
              <Layout className="w-12 h-12 text-white/40 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Registry Found</h3>
              <p className="text-white/70 mb-4">Create a registry to use the layout planner feature.</p>
              <button
                onClick={() => (window.location.href = '/create')}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600"
              >
                Create Registry
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}